import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ease } from "@/components/motion/Reveal";

const KEY = "nivo_intro_seen";
const HOLD = 2000; // Zeit bis die Wipe-Animation startet
const WIPE = 900; // Dauer der Reveal-Animation

const safeGet = (k) => {
  try { return window.sessionStorage.getItem(k); } catch { return null; }
};
const safeSet = (k, v) => {
  try { window.sessionStorage.setItem(k, v); } catch { /* Sandbox: ignorieren */ }
};

export const Intro = () => {
  const [mounted, setMounted] = useState(() => {
    if (typeof window === "undefined") return false;
    if (window.location.search.includes("nointro")) return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    // Auf Touch-/Mobilgeräten KEINE Intro – verhindert jegliche Scroll-Blockade
    const isTouch =
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.innerWidth < 768;
    if (isTouch) return false;
    return !safeGet(KEY);
  });
  const [leaving, setLeaving] = useState(false);
  const [count, setCount] = useState(0);

  // Ablaufsteuerung über setTimeout (auf echten Geräten absolut zuverlässig)
  useEffect(() => {
    if (!mounted) return undefined;
    const tLeave = setTimeout(() => {
      safeSet(KEY, "1");
      setLeaving(true);
    }, HOLD);
    // Fallback-Aushängen, falls onTransitionEnd nicht feuert
    const tUnmount = setTimeout(() => setMounted(false), HOLD + WIPE + 400);
    return () => {
      clearTimeout(tLeave);
      clearTimeout(tUnmount);
    };
  }, [mounted]);

  // Scroll-Sperre NUR über body overflow – zuverlässig reversibel.
  // Bewusst KEIN lenis.stop(): das blockiert auf Mobile das Touch-Scrollen.
  useEffect(() => {
    if (mounted && !leaving) {
      window.scrollTo(0, 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mounted, leaving]);

  // Rein kosmetischer Ladezähler
  useEffect(() => {
    if (!mounted) return undefined;
    const start = performance.now();
    const dur = HOLD - 300;
    let raf;
    const tick = (t) => {
      setCount(Math.min(100, Math.round(((t - start) / dur) * 100)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mounted]);

  if (!mounted) return null;

  return (
    <div
      data-testid="intro-overlay"
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[color:var(--bg)]"
      onTransitionEnd={(e) => {
        if (e.propertyName === "transform") setMounted(false);
      }}
      style={{
        transform: leaving ? "translateY(-100%)" : "translateY(0)",
        transition: `transform ${WIPE}ms cubic-bezier(0.76,0,0.24,1)`,
        pointerEvents: leaving ? "none" : "auto",
      }}
    >
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[color:var(--plum)] opacity-[0.16] blur-[170px]" />

      <motion.img
        src="/nivo-logo-white.png"
        alt="NIVO GARAGE"
        width="744"
        height="390"
        className="relative w-[230px] sm:w-[300px] h-auto"
        initial={{ opacity: 0, scale: 0.92, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, ease, delay: 0.1 }}
      />

      <motion.div
        className="relative mt-8 h-px w-[180px] origin-center bg-[color:var(--violet)]"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 0.9 }}
        transition={{ duration: 1.1, ease, delay: 0.35 }}
      />

      <span className="absolute bottom-8 right-8 mono text-[13px] tabular-nums text-[color:var(--ink-3)]" data-testid="intro-counter">
        {String(count).padStart(3, "0")}
      </span>
    </div>
  );
};
